import {integer} from "@/statistics/utils/format";

const DATE_TIME = new Intl.DateTimeFormat('fr-FR', {dateStyle: 'short', timeStyle: 'short'});
const DATE = new Intl.DateTimeFormat('fr-FR', {dateStyle: 'short'});

/** « 17/10/2026 18:30 », ou « — » quand il n'y a pas de date (aucune connexion par exemple) */
export const dateTime = value => (value ? DATE_TIME.format(new Date(value)) : '—');
export const date = value => (value ? DATE.format(new Date(value)) : '—');

/** AAAA-MM-JJ → JJ/MM */
export const shortDay = day => `${day.slice(8, 10)}/${day.slice(5, 7)}`;

const TIME = new Intl.DateTimeFormat('fr-FR', {timeStyle: 'medium'});
const MINUTE = new Intl.DateTimeFormat('fr-FR', {hour: '2-digit', minute: '2-digit'});
const NUMBER = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 1});

/** « 18:30:12 » */
export const time = value => (value ? TIME.format(new Date(value)) : '—');
/** « 18:30 » */
export const minute = value => MINUTE.format(new Date(value));

const DAY_MINUTE = new Intl.DateTimeFormat('fr-FR', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'});
/** « 17/10 18:30 » */
export const dayMinute = value => DAY_MINUTE.format(new Date(value));

/** Taille en octets → « 512 Mo » */
export const bytes = value => {
    if (value === null || value === undefined) return '—';
    const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
    let size = value;
    let index = 0;
    while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index++;
    }
    return `${NUMBER.format(index === 0 ? Math.round(size) : size)} ${units[index]}`;
};

/** Octets → mégaoctets entiers, pour les graphiques */
export const megabytes = value => (value === null || value === undefined ? null : Math.round(value / 1048576));

/** Durée en secondes → « 3 j 4 h », « 12 min » */
export const duration = seconds => {
    if (seconds < 60) return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h ${String(minutes % 60).padStart(2, '0')}`;
    return `${Math.floor(hours / 24)} j ${hours % 24} h`;
};

/** Durée en millisecondes → « 420 ms », « 1,2 s » */
export const milliseconds = value => (value < 1000 ? `${integer(Math.round(value))} ms` : `${integer(Math.round(value / 100) / 10)} s`);

/** Durée en millisecondes → « 45 s », « 3 min 20 s » (temps passé sur une page, durée d'une session) */
export const longDuration = value => {
    const seconds = Math.round(value / 1000);
    if (seconds < 60) return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ${String(seconds % 60).padStart(2, '0')} s`;
    return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
};

/**
 * Valeur affichée selon son genre, tel que les containers le décrivent (text, number, percent, datetime, milliseconds, bytes, duration, badge).
 * Une valeur absente s'affiche « — ».
 */
export const cell = (kind, value) => {
    if (value === null || value === undefined || value === '') return '—';
    switch (kind) {
        case 'number': return integer(value);
        case 'percent': return `${integer(value)} %`;
        case 'datetime': return dateTime(value);
        case 'milliseconds': return milliseconds(value);
        case 'bytes': return bytes(value);
        case 'duration': return longDuration(value);
        default: return String(value);
    }
};

/** Niveaux de l'architecture : les fronts appellent la passerelle, qui joint les services du back */
export const TIERS = {
    front: {label: 'Front'},
    gateway: {label: 'Passerelle'},
    back: {label: 'Back'},
};

/** Comment un container est joint depuis l'extérieur du réseau docker */
export const EXPOSURES = {
    public: {label: 'Public'},
    gateway: {label: 'Via la passerelle'},
    internal: {label: 'Interne, non exposé'},
};

/** Couleurs des courbes d'un container, toutes lisibles sur fond blanc : un container garde la sienne d'un graphique à l'autre */
export const CONTAINER_COLORS = ['#24396b', '#2aa3a3', '#d4a90a', '#4a67ad', '#ef9400', '#3fbd63', '#9b6bd1', '#ea4e2c', '#6b7280', '#b5651d', '#d6336c', '#5c940d', '#212529', '#a61e4d'];
export const containerColor = index => CONTAINER_COLORS[index % CONTAINER_COLORS.length];

/** Teinte d'une jauge : « ok » sous le premier seuil, « warning » entre les deux, « critical » au-delà (seuils : {warning, critical}) */
export const usageTone = (percent, thresholds) => (percent >= thresholds.critical ? 'critical' : percent >= thresholds.warning ? 'warning' : 'ok');

/** Libellé et teinte (ok, warning, critical, muted) de l'état d'un container */
export const containerBadge = container => {
    if (container.status === 'down') return {label: 'Hors ligne', tone: 'critical'};
    if (container.status === 'unsupported') return {label: 'Non supervisé', tone: 'muted'};
    return {
        ok: {label: 'Sain', tone: 'ok'},
        warning: {label: 'Alerte', tone: 'warning'},
        critical: {label: 'Critique', tone: 'critical'},
    }[container.health] ?? {label: 'Inconnu', tone: 'muted'};
};

// Groupes qui ne sont pas des clients : salle d'attente des nouveaux utilisateurs, bannis, clients qui signent, super admins
const GROUP_LABELS = {newer: "Nouveaux", banned: "Bannis", client: "Clients (signature)", super_admin: "Super administrateurs"};
export const groupLabel = name => GROUP_LABELS[name] ?? name;
